import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Avatar, Spin, message } from 'antd';
import {
    RobotOutlined,
    SendOutlined,
    CloseOutlined,
    UserOutlined,
    MessageOutlined,
    ThunderboltOutlined,
    LoginOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    ExclamationCircleOutlined,
    WifiOutlined
} from '@ant-design/icons';
import { getImg } from '../../Api/getImg';
import popupSound from "../../assets/bubble-pop.mp3";
import { useDoctorQuery } from '../../hooks/useDoctorQuery';

const { TextArea } = Input;

const formatMessageContent = (content) => {
    if (!content) return content;

    // Handle line breaks first
    let formattedContent = content.replace(/\\n/g, '\n').replace(/\n/g, '<br/>');

    // Handle bold text (**text**)
    formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Handle italic text (*text*)
    formattedContent = formattedContent.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');

    // Handle underline (__text__)
    formattedContent = formattedContent.replace(/__(.*?)__/g, '<u>$1</u>');

    // Handle strikethrough (~~text~~)
    formattedContent = formattedContent.replace(/~~(.*?)~~/g, '<del>$1</del>');

    // Handle code blocks (```code```)
    formattedContent = formattedContent.replace(/```(.*?)```/gs, '<pre class="bg-gray-100 p-2 rounded text-xs overflow-x-auto"><code>$1</code></pre>');

    // Handle inline code (`code`)
    formattedContent = formattedContent.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-xs">$1</code>');

    // Handle headers (## Header)
    formattedContent = formattedContent.replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold mt-3 mb-2">$1</h3>');
    formattedContent = formattedContent.replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>');
    formattedContent = formattedContent.replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>');

    // Handle lists (- item or * item)
    formattedContent = formattedContent.replace(/^[\s]*[-*]\s(.*)$/gm, '<li class="ml-4 list-disc">$1</li>');

    // Handle numbered lists (1. item)
    formattedContent = formattedContent.replace(/^[\s]*\d+\.\s(.*)$/gm, '<li class="ml-4 list-decimal">$1</li>');

    // Wrap consecutive list items in ul/ol tags
    formattedContent = formattedContent.replace(/(<li class="ml-4 list-disc">.*<\/li>)/gs, '<ul class="space-y-1 my-2">$1</ul>');
    formattedContent = formattedContent.replace(/(<li class="ml-4 list-decimal">.*<\/li>)/gs, '<ol class="space-y-1 my-2">$1</ol>');

    // Handle horizontal rules (--- or ─────)
    formattedContent = formattedContent.replace(/^-{3,}$/gm, '<hr class="my-3 border-gray-300"/>');
    formattedContent = formattedContent.replace(/^─{3,}$/gm, '<hr class="my-3 border-gray-300"/>');

    // Handle links [text](url)
    formattedContent = formattedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');

    // Handle email addresses
    formattedContent = formattedContent.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1" class="text-primary hover:underline">$1</a>');

    // Handle phone numbers (basic pattern)
    formattedContent = formattedContent.replace(/(\+?[\d\s\-\(\)]{10,})/g, (match) => {
        if (match.replace(/[\s\-\(\)]/g, '').length >= 10) {
            return `<a href="tel:${match}" class="text-primary hover:underline">${match}</a>`;
        }
        return match;
    });

    return formattedContent;
};

const EnhancedMessage = ({ message, isUser, user, isError }) => {
    const formattedContent = formatMessageContent(message.content);

    return (
        <div
            className={`p-3 rounded-2xl text-sm break-words shadow-lg transition-all duration-200 hover:shadow-xl ${isError
                ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
                : isUser
                    ? 'bg-primary text-white rounded-br-md'
                    : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
                }`}
            style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: formattedContent }}
        />
    );
};

// Network error detection utility
const getNetworkErrorMessage = (error) => {
    if (!navigator.onLine) {
        return "🔌 **Network Connection Lost**\n\nIt looks like you're offline. Please check your internet connection and try again.";
    }

    if (error.name === 'TypeError' && error.message?.includes('fetch')) {
        return "🌐 **Network Request Failed**\n\nUnable to connect to the server. This could be due to:\n- Poor internet connection\n- Server temporarily unavailable\n- Firewall or network restrictions\n\nPlease try again in a moment.";
    }

    if (error.message.includes('timeout')) {
        return "⏱️ **Request Timeout**\n\nThe server is taking too long to respond. This might be due to high server load or network issues.\n\nPlease try again.";
    }

    return "❌ **Connection Error**\n\nSomething went wrong with the connection. Please check your internet and try again.";
};

const AIAssistant = ({ isOpen, onToggle, user, slug, onLogin }) => {

    const [messages, setMessages] = useState([]);
    const [hasThread, setHasThread] = useState(false);
    const [threadCheckComplete, setThreadCheckComplete] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCreatingThread, setIsCreatingThread] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [suggestedQuestions, setSuggestedQuestions] = useState([]);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        hasMore: true
    });
    const { data: doctor } = useDoctorQuery();
   
    const shouldScrollRef = useRef(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const isAuthorized =
        !!localStorage.getItem(`userTkn-${slug}`) ||
        !!sessionStorage.getItem(`userTkn-${slug}`);

    const getAuthToken = () => {
        return localStorage.getItem(`userTkn-${slug}`) ||
            sessionStorage.getItem(`userTkn-${slug}`);
    };

    const scrollToBottom = () => {
        if (shouldScrollRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current && isAuthorized && hasThread) {
            inputRef.current.focus();
        }
    }, [isOpen, isAuthorized, hasThread]);

    useEffect(() => {
        if (isOpen && isAuthorized) {
            checkForExistingThread();
        }
    }, [isOpen, isAuthorized]);

    const audioRef = useRef(new Audio(popupSound));

    const playNotificationSound = () => {
        try {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(error => {
            });
        } catch (error) {
        }
    };

    // Enhanced error bubble message creator
    const createErrorMessage = (error, context = 'general') => {
        let errorContent = getNetworkErrorMessage(error);

        if (context === 'thread_creation') {
            errorContent = "🔧 **Failed to Create Chat Thread**\n\n" + errorContent + "\n\n*Click 'Start Conversation' to try again.*";
        } else if (context === 'message_send') {
            errorContent = "📤 **Message Send Failed**\n\n" + errorContent + "\n\n*Your message wasn't sent. Please try sending it again.*";
        } else if (context === 'load_messages') {
            errorContent = "📥 **Failed to Load Messages**\n\n" + errorContent;
        }

        return {
            id: Date.now() + Math.random(),
            type: 'error',
            content: errorContent,
            timestamp: new Date(),
            isError: true
        };
    };

    const checkForExistingThread = async () => {
        if (!isAuthorized) {
            setThreadCheckComplete(true);
            return;
        }

        try {
            setIsLoadingMessages(true);
            const token = getAuthToken();

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            const response = await fetch(`https://api.dreasey.com/api/DrEasyAi/GetThreadMessages?PageNumber=1&PageSize=${pagination.pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                setHasThread(true);

                const transformedMessages = (data.messages || []).map((msg, index) => ({
                    id: `${data.threadId}-${index}-${msg.createdAt}` || Date.now() + Math.random(),
                    type: msg.role === 'user' ? 'user' : 'ai',
                    content: msg.content || '',
                    timestamp: new Date(msg.createdAt),
                    role: msg.role
                }));

                setMessages(transformedMessages.reverse());
                setPagination(prev => ({
                    ...prev,
                    pageNumber: data.pageNumber || 1,
                    hasMore: (data.totalMessages || 0) > (data.pageNumber || 1) * (data.pageSize || 10)
                }));
            } else if (response.status === 404) {
                setHasThread(false);
                setMessages([]);
            } else if (response.status >= 500) {
                throw new Error('Server error - please try again later');
            } else if (response.status === 401) {
                message.error('Session expired. Please login again.');
                setMessages([createErrorMessage(new Error('Authentication failed'), 'load_messages')]);
            } else {
                throw new Error(`Failed to fetch messages: ${response.status}`);
            }
        } catch (error) {
            console.error('Error checking for thread:', error);

            if (error.name === 'AbortError') {
                const timeoutError = new Error('Request timeout');
                timeoutError.message = 'timeout';
                setMessages([createErrorMessage(timeoutError, 'load_messages')]);
            } else {
                setMessages([createErrorMessage(error, 'load_messages')]);
            }

            setHasThread(false);
        } finally {
            setIsLoadingMessages(false);
            setThreadCheckComplete(true);
        }
    };

    const createThread = async () => {
        if (!isAuthorized) return;

        try {
            setIsCreatingThread(true);
            const token = getAuthToken();

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            const response = await fetch('https://api.dreasey.com/api/DrEasyAi/CreateThread', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                setHasThread(true);
                setMessages([]);
                setPagination({
                    pageNumber: 1,
                    pageSize: 20,
                    hasMore: false
                });
                message.success('Chat thread created successfully!');
            } else if (response.status >= 500) {
                throw new Error('Server error - please try again later');
            } else {
                const errorText = await response.text();
                throw new Error(`Failed to create thread: ${response.status} - ${errorText}`);
            }
        } catch (error) {
            console.error('Error creating thread:', error);

            if (error.name === 'AbortError') {
                const timeoutError = new Error('Request timeout');
                timeoutError.message = 'timeout';
                setMessages([createErrorMessage(timeoutError, 'thread_creation')]);
            } else {
                setMessages([createErrorMessage(error, 'thread_creation')]);
            }
        } finally {
            setIsCreatingThread(false);
        }
    };

    const loadMoreMessages = async () => {
        if (!pagination.hasMore || isLoadingMessages) return;

        try {
            setIsLoadingMessages(true);
            shouldScrollRef.current = false;

            const token = getAuthToken();
            const nextPage = pagination.pageNumber + 1;

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            const response = await fetch(`https://api.dreasey.com/api/DrEasyAi/GetThreadMessages?PageNumber=${nextPage}&PageSize=${pagination.pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();

                const transformedMessages = (data.messages || []).map((msg, index) => ({
                    id: `${data.threadId}-${data.pageNumber}-${index}-${msg.createdAt}` || Date.now() + Math.random(),
                    type: msg.role === 'user' ? 'user' : 'ai',
                    content: msg.content || '',
                    timestamp: new Date(msg.createdAt),
                    role: msg.role
                }));

                setMessages(prev => [...transformedMessages.reverse(), ...prev]);
                setPagination(prev => ({
                    ...prev,
                    pageNumber: nextPage,
                    hasMore: (data.totalMessages || 0) > nextPage * (data.pageSize || 10)
                }));
            } else {
                throw new Error(`Failed to load more messages: ${response.status}`);
            }
        } catch (error) {
            console.error('Error loading more messages:', error);

            if (error.name === 'AbortError') {
                const timeoutError = new Error('Request timeout');
                timeoutError.message = 'timeout';
                message.error('Request timed out. Please try again.');
            } else {
                message.error('Failed to load more messages. Please check your connection.');
            }
        } finally {
            setIsLoadingMessages(false);
        }
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || !hasThread) return;

        if (!isAuthorized) {
            message.warning('Please login to continue using the AI assistant.');
            return;
        }

        shouldScrollRef.current = true;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: inputValue.trim(),
            timestamp: new Date(),
            role: 'user'
        };

        setMessages(prev => [...prev, userMessage]);
        const messageToSend = inputValue.trim();
        setInputValue('');
        setIsLoading(true);

        try {
            const token = getAuthToken();
            const formData = new FormData();
            formData.append('message', messageToSend);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for AI responses

            const response = await fetch('https://api.dreasey.com/api/DrEasyAi/SendMessage', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                },
                body: formData,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                const aiResponse = {
                    id: Date.now() + 1,
                    type: 'ai',
                    content: data.responseMessage || 'Response received',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, aiResponse]);
                playNotificationSound();
            } else if (response.status >= 500) {
                throw new Error('Server error - please try again later');
            } else if (response.status === 401) {
                throw new Error('Session expired - please login again');
            } else {
                const errorText = await response.text();
                throw new Error(`Request failed: ${response.status} - ${errorText}`);
            }
        } catch (error) {
            console.error('Error sending message:', error);

            // Remove the user message that failed to send
            setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));

            // Add error message bubble
            let errorMessage;
            if (error.name === 'AbortError') {
                const timeoutError = new Error('Request timeout');
                timeoutError.message = 'timeout';
                errorMessage = createErrorMessage(timeoutError, 'message_send');
            } else {
                errorMessage = createErrorMessage(error, 'message_send');
            }

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    useEffect(() => {
        if (isOpen && isAuthorized && hasThread) {
            fetchSuggestedQuestions();
        }
    }, [isOpen, isAuthorized, hasThread]);

    const fetchSuggestedQuestions = async () => {
        try {
            const token = getAuthToken();

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            const response = await fetch("https://api.dreasey.com/api/DrEasyAi/GetSuggested", {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': '*/*'
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                const questions = data.map(q => ({ type: q.type, text: q.text }));
                setSuggestedQuestions(questions);
            } else {
                console.error('Failed to fetch suggested questions:', response.status);
            }
        } catch (error) {
            console.error("Error fetching suggested questions:", error);
            // Don't show error for suggested questions as it's not critical
        }
    };

    const handleSuggestedQuestion = async (questionObj) => {
        if (!isAuthorized || !hasThread) return;

        const { text, type } = questionObj;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: text,
            timestamp: new Date(),
            role: 'user'
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);

        try {
            const token = getAuthToken();

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch("https://api.dreasey.com/api/DrEasyAi/AnswerSuggested", {
                method: "POST",
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ questionType: type }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                const aiResponse = {
                    id: Date.now() + 1,
                    type: 'ai',
                    content: data.answer || "No answer provided.",
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, aiResponse]);
                playNotificationSound();
            } else {
                throw new Error(`Failed to get AI response: ${response.status}`);
            }
        } catch (error) {
            console.error("Error handling suggested question:", error);

            // Remove the user message that failed to send
            setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));

            // Add error message bubble
            let errorMessage;
            if (error.name === 'AbortError') {
                const timeoutError = new Error('Request timeout');
                timeoutError.message = 'timeout';
                errorMessage = createErrorMessage(timeoutError, 'message_send');
            } else {
                errorMessage = createErrorMessage(error, 'message_send');
            }

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = () => {
        if (onLogin) {
            onLogin();
        } else {
            window.location.href = `/${slug}/auth`;
        }
    };

    const SuggestedQuestions = () => {
        return (
            <div className="px-4 pb-2 border-t bg-white">
                <div className="py-2">
                    <div className="flex space-x-2 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {suggestedQuestions.map((q, index) => (
                            <button
                                key={index}
                                onClick={() => handleSuggestedQuestion(q)}
                                disabled={!isAuthorized || !hasThread}
                                className={`flex-shrink-0 px-3 py-1.5 text-xs rounded-full border transition-all duration-200 whitespace-nowrap ${!isAuthorized || !hasThread
                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-200 hover:scale-105'
                                    }`}
                            >
                                {q.text}
                            </button>
                        ))}
                    </div>
                </div>

                <style jsx>{`
                    .scrollbar-hide::-webkit-scrollbar {
                        display: none;
                    }
                `}</style>
            </div>
        );
    };

    const renderWelcomeScreen = () => (
        <div className="flex-1 flex flex-col bg-white rounded-b-xl">
            <div className="flex-1 flex flex-col items-center rounded-b-xl justify-center p-6 bg-gradient-to-br from-blue-50/80 via-white to-purple-50/60 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-10 left-10 w-20 h-20 bg-blue-200/30 rounded-full animate-pulse"></div>
                    <div className="absolute top-20 right-16 w-12 h-12 bg-purple-200/40 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
                    <div className="absolute bottom-20 left-20 w-16 h-16 bg-green-200/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                    <div className="absolute bottom-32 right-8 w-8 h-8 bg-yellow-200/40 rounded-full animate-bounce" style={{ animationDelay: '1.5s' }}></div>
                </div>

                <div className="text-center space-y-2 relative z-10 max-w-sm mx-auto">
                    <div className="relative mx-auto w-24 h-24 group animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                        <div className="absolute inset-0 bg-gradient-to-r from-primary to-blue-600 rounded-full animate-ping opacity-20"></div>
                        <div className="absolute inset-2 bg-gradient-to-r from-primary/50 to-blue-600/50 rounded-full animate-pulse"></div>

                        <Avatar
                            icon={<RobotOutlined />}
                            size={96}
                            className="bg-gradient-to-r from-primary to-blue-600 shadow-2xl border-4 border-white transform transition-all duration-300 hover:scale-110 hover:rotate-3 relative z-10"
                        />

                        <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-green-400 rounded-full border-4 border-white animate-pulse shadow-lg">
                            <div className="absolute inset-1 bg-green-300 rounded-full animate-ping"></div>
                        </div>

                        <div className="absolute -top-2 -left-2 w-2 h-2 bg-blue-400 rounded-full animate-bounce opacity-60" style={{ animationDelay: '0.3s' }}></div>
                        <div className="absolute -top-1 -right-3 w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce opacity-70" style={{ animationDelay: '0.7s' }}></div>
                        <div className="absolute -bottom-2 -left-3 w-1 h-1 bg-green-400 rounded-full animate-bounce opacity-50" style={{ animationDelay: '1.1s' }}></div>
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                        <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-3 leading-tight">
                            Welcome to AI Medical Assistant
                        </h3>
                        <div className="h-1 w-16 bg-gradient-to-r from-primary to-blue-600 rounded-full mx-auto mb-4 animate-pulse"></div>
                        <p className="text-sm text-gray-600 leading-relaxed max-w-xs mx-auto">
                            Start your conversation with our intelligent medical assistant. Get instant help with
                            <span className="font-semibold text-primary"> appointments</span>,
                            <span className="font-semibold text-primary"> patient management</span>, and
                            <span className="font-semibold text-primary"> clinic operations</span>.
                        </p>
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<PlayCircleOutlined className="animate-pulse" />}
                            onClick={createThread}
                            loading={isCreatingThread}
                            className="bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-primary border-0 shadow-2xl px-10 py-3 h-auto rounded-full font-semibold text-base transform transition-all duration-300 hover:scale-105 hover:shadow-primary/50 group"
                        >
                            <span className="flex items-center space-x-2">
                                <span>{isCreatingThread ? 'Creating Magic...' : 'Start Conversation'}</span>
                                {!isCreatingThread && <span className="text-lg">✨</span>}
                            </span>
                        </Button>
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
                        <div className="grid grid-cols-2 gap-3 mt-6 text-xs">
                            <div className="flex items-center space-x-2 text-gray-600 group hover:text-primary transition-colors duration-200">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="font-medium">24/7 Available</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600 group hover:text-primary transition-colors duration-200">
                                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                                <span className="font-medium">Instant Responses</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600 group hover:text-primary transition-colors duration-200">
                                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                                <span className="font-medium">Medical Expertise</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600 group hover:text-primary transition-colors duration-200">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{ animationDelay: '0.9s' }}></div>
                                <span className="font-medium">Smart Assistance</span>
                            </div>
                        </div>
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '1.4s' }}>
                        <p className="text-xs text-gray-400 mt-4 flex items-center justify-center space-x-1">
                            <span>✨</span>
                            <span>Powered by advanced AI technology</span>
                            <span>✨</span>
                        </p>
                    </div>
                </div>
            </div>

            <style jsx>{`
            @keyframes fade-in-up {
                from {
                    opacity: 0;
                    transform: translateY(30px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            .animate-fade-in-up {
                opacity: 0;
                animation: fade-in-up 0.8s ease-out forwards;
            }

            @keyframes float {
                0%, 100% {
                    transform: translateY(0px);
                }
                50% {
                    transform: translateY(-10px);
                }
            }

            .animate-float {
                animation: float 3s ease-in-out infinite;
            }

            .scrollbar-hide::-webkit-scrollbar {
                display: none;
            }
        `}</style>
        </div>
    );

    const renderChatScreen = () => (
        <div className="flex-1 flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
                {pagination.hasMore && (
                    <div className="flex justify-center">
                        <Button
                            type="link"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={loadMoreMessages}
                            loading={isLoadingMessages}
                            className="text-gray-500 hover:text-primary"
                        >
                            {isLoadingMessages ? 'Loading...' : 'Load previous messages'}
                        </Button>
                    </div>
                )}

                {messages.map((message, index) => {
                    const isUser = message.type === 'user' || message.role === 'user';
                    const isError = message.type === 'error' || message.isError;

                    return (
                        <div
                            key={message.id || index}
                            className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}
                            style={{ animationDelay: `${index * 0.1}s` }}
                        >
                            <div className={`flex items-start space-x-2 max-w-[90%] my-1 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                                {isUser ? (
                                    <div className="relative">
                                        <Avatar
                                            src={user?.imageId ? getImg(user.imageId) : null}
                                            icon={!user?.imageId ? <UserOutlined /> : null}
                                            size={36}
                                            className="flex-shrink-0 shadow-lg border-1 border-primary mt-8"
                                        />
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Avatar
                                            icon={isError ? <ExclamationCircleOutlined /> : <RobotOutlined />}
                                            size={36}
                                            className={`flex-shrink-0 shadow-lg border-2 border-white mt-10 ${isError
                                                ? 'bg-red-500'
                                                : 'bg-gradient-to-r from-primary to-blue-600'
                                                }`}
                                        />
                                        <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${isError ? 'bg-red-400' : 'bg-green-400'
                                            }`}></div>
                                    </div>
                                )}

                                <div className="flex flex-col space-y-1 w-full">
                                    <EnhancedMessage message={message} isUser={isUser} user={user} isError={isError} />

                                    {message.timestamp && (
                                        <div className={`text-xs text-gray-400 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
                                            {new Date(message.timestamp).toLocaleString([], {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true,
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {isLoading && (
                    <div className="flex justify-start animate-fade-in">
                        <div className="flex items-start space-x-3">
                            <div className="relative">
                                <Avatar
                                    icon={<RobotOutlined />}
                                    size={36}
                                    className="bg-gradient-to-r from-primary to-blue-600 flex-shrink-0 shadow-lg border-2 border-white"
                                />
                                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
                            </div>
                            <div className="bg-white p-4 rounded-2xl rounded-bl-md shadow-lg border border-gray-100">
                                <div className="flex items-center space-x-1">
                                    <div className="flex space-x-1">
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <SuggestedQuestions />

            <div className="p-4 border-t bg-white">
                <div className="flex space-x-2">
                    <TextArea
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your message..."
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        className="flex-1 border rounded-full"
                        disabled={isLoading || !hasThread}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || isLoading || !hasThread}
                        className="bg-primary hover:bg-primary/80 rounded-full p-1 flex justify-center items-center"
                    />
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div
                className={`fixed bottom-8 right-10 md:right-16 z-[9999] cursor-pointer group ${isOpen ? "hidden md:flex" : "block"}`}
                onClick={onToggle}
            >
                <div className="relative">
                    <div className={`absolute inset-0 bg-primary rounded-full ${!isOpen ? 'animate-ping' : ''} opacity-30`}></div>
                    <div className={`relative bg-primary hover:from-blue-600 hover:to-primary text-white rounded-full p-3 shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-primary/50 ${isOpen ? 'px-4' : ''}`}>
                        {isOpen ? (
                            <MessageOutlined className="text-sm" />
                        ) : (
                            <MessageOutlined className="text-3xl" />
                        )}
                    </div>
                    <div className={`absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white ${!isOpen ? 'animate-pulse' : ''}`}></div>
                </div>

                <div className={`absolute bottom-full -left-12 md:-left-20 mb-2 px-3 py-1 bg-gray-900 text-white ${isOpen ? 'opacity-0' : 'opacity-100'} text-[10px] md:text-xs rounded-lg whitespace-nowrap`}>
                    {isOpen ? '' : 'Chat with AI Assistant'}
                    <div className="absolute bottom-[-4px] right-9 md:right-10 w-2 h-2 bg-black transform rotate-45"></div>
                </div>
            </div>

            {isOpen && (
                <div className="fixed bottom-16 right-6 md:right-28 z-50 w-[340px] md:w-96 h-[500px] bg-white rounded-2xl shadow-2xl border flex flex-col">
                    <div className="bg-gradient-to-r from-primary to-blue-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
                        <div className="relative">
                            <Avatar
                                icon={<RobotOutlined />}
                                className="bg-white/20 backdrop-blur-sm border-2 border-white/30"
                                size={36}
                            />
                            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                        </div>
                        <div>
                            <h3 className="font-semibold text-sm flex items-center">
                                AI Medical Assistant
                                <ThunderboltOutlined className="ml-1 text-yellow-300 text-xs" />
                            </h3>
                            <p className="text-xs opacity-90">
                                {!isAuthorized
                                    ? 'Login required'
                                    : !threadCheckComplete
                                        ? 'Loading...'
                                        : hasThread
                                            ? 'Always here to help'
                                            : 'Ready to start'}
                            </p>
                        </div>
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={onToggle}
                            className="text-white hover:bg-blue-500"
                            size="small"
                        />
                    </div>

                    {doctor.isLocked?( <div className="flex-1 flex flex-col items-center justify-center space-y-3 py-2">
                        <h2>Doctor is unavilable</h2>
                            <p className="text-sm text-gray-600 text-center">
                                You can't use Ai Assisstant 
                            </p>
                          
                        </div>):
                    !isAuthorized ? (
                        <div className="flex-1 flex flex-col items-center justify-center space-y-3 py-2">
                            <p className="text-sm text-gray-600 text-center">
                                Please login to chat with AI assistant
                            </p>
                            <Button
                                icon={<LoginOutlined />}
                                onClick={handleLogin}
                                className="border border-primary text-primary bg-white hover:bg-primary/10 hover:text-white"
                            >
                                Login to Continue
                            </Button>
                        </div>
                    ) : !threadCheckComplete ? (
                        <div className="flex-1 flex items-center justify-center">
                            <Spin size="large" />
                        </div>
                    ) : hasThread ? (
                        renderChatScreen()
                    ) : (
                        renderWelcomeScreen()
                    )}
                </div>
            )}
        </>
    );
};

export default AIAssistant;